<template>
  <div>
    <a-page-header title="知识类型管理">
      <template #extra>
        <a-button type="primary" @click="handleCreate">
          <template #icon><PlusOutlined /></template>
          新建
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'is_protected'">
            <a-tag v-if="record.is_protected" color="red">受保护</a-tag>
            <a-tag v-else color="default">公开</a-tag>
          </template>
          <template v-if="column.key === 'action'">
            <a-button type="link" @click="handleEdit(record)">编辑</a-button>
            <a-popconfirm title="确定删除吗？" @confirm="handleDelete(record.id)">
              <a-button type="link" danger>删除</a-button>
            </a-popconfirm>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="modalVisible"
      :title="isEdit ? '编辑知识类型' : '新建知识类型'"
      @ok="handleSubmit"
      @cancel="handleCancel"
    >
      <a-form :model="formData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="类型名称" required>
          <a-input v-model:value="formData.type_name" placeholder="请输入类型名称" />
        </a-form-item>
        <a-form-item label="类型代码" required>
          <a-input v-model:value="formData.type_code" placeholder="请输入类型代码" />
        </a-form-item>
        <a-form-item label="排序号">
          <a-input-number v-model:value="formData.sort_order" :min="0" style="width: 100%" />
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea v-model:value="formData.remark" :rows="3" placeholder="请输入备注" />
        </a-form-item>
        <a-form-item label="仅管理员可见">
          <a-switch
            v-model:checked="formData.is_protected"
            :checked-value="1"
            :unchecked-value="0"
            checked-children="开启"
            un-checked-children="关闭"
          />
          <span style="margin-left: 8px; color: #999; font-size: 12px">
            开启后，editor 角色无法查看此类型下的知识
          </span>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import { getKnowledgeTypes, createKnowledgeType, updateKnowledgeType, deleteKnowledgeType } from '../api/knowledgeType';

export default {
  name: 'KnowledgeType',
  components: {
    PlusOutlined
  },
  setup() {
    const loading = ref(false);
    const dataSource = ref([]);
    const modalVisible = ref(false);
    const isEdit = ref(false);
    const currentId = ref(null);

    const formData = reactive({
      type_name: '',
      type_code: '',
      sort_order: 0,
      remark: '',
      is_protected: 0
    });

    const columns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
      { title: '类型名称', dataIndex: 'type_name', key: 'type_name' },
      { title: '类型代码', dataIndex: 'type_code', key: 'type_code' },
      { title: '排序号', dataIndex: 'sort_order', key: 'sort_order', width: 100 },
      { title: '受保护', dataIndex: 'is_protected', key: 'is_protected', width: 100 },
      { title: '创建时间', dataIndex: 'create_time', key: 'create_time', width: 180 },
      { title: '操作', key: 'action', width: 150, fixed: 'right' }
    ];

    const loadData = async () => {
      loading.value = true;
      try {
        const res = await getKnowledgeTypes();
        dataSource.value = res.data || [];
      } catch (error) {
        message.error('加载数据失败');
      } finally {
        loading.value = false;
      }
    };

    const handleCreate = () => {
      isEdit.value = false;
      currentId.value = null;
      Object.assign(formData, {
        type_name: '',
        type_code: '',
        sort_order: 0,
        remark: '',
        is_protected: 0
      });
      modalVisible.value = true;
    };

    const handleEdit = (record) => {
      isEdit.value = true;
      currentId.value = record.id;
      Object.assign(formData, {
        type_name: record.type_name,
        type_code: record.type_code,
        sort_order: record.sort_order,
        remark: record.remark || '',
        is_protected: record.is_protected || 0
      });
      modalVisible.value = true;
    };

    const handleDelete = async (id) => {
      try {
        await deleteKnowledgeType(id);
        message.success('删除成功');
        loadData();
      } catch (error) {
        message.error('删除失败');
      }
    };

    const handleSubmit = async () => {
      try {
        if (isEdit.value) {
          await updateKnowledgeType(currentId.value, formData);
          message.success('更新成功');
        } else {
          await createKnowledgeType(formData);
          message.success('创建成功');
        }
        modalVisible.value = false;
        loadData();
      } catch (error) {
        message.error(isEdit.value ? '更新失败' : '创建失败');
      }
    };

    const handleCancel = () => {
      modalVisible.value = false;
    };

    onMounted(() => {
      loadData();
    });

    return {
      loading,
      dataSource,
      modalVisible,
      isEdit,
      formData,
      columns,
      handleCreate,
      handleEdit,
      handleDelete,
      handleSubmit,
      handleCancel
    };
  }
};
</script>
